/**
 * Minimal sRGB ⇄ OKLCH conversion (Björn Ottosson's OKLab).
 *
 * Used to NORMALIZE external brand colors (a bank's Pluggy `connectorPrimaryColor`)
 * into the design-system envelope: keep the recognizable hue, replace lightness +
 * chroma with the institution curve (palette.md §07). Hue is perceptually uniform
 * in OKLCH, so "keep the hue" actually keeps the hue a human sees.
 */

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
}

function parse(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((d) => d + d).join('') : h;
  return [
    parseInt(full.slice(0, 2), 16) / 255,
    parseInt(full.slice(2, 4), 16) / 255,
    parseInt(full.slice(4, 6), 16) / 255,
  ];
}

function toHex(r: number, g: number, b: number): string {
  const ch = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${ch(r)}${ch(g)}${ch(b)}`;
}

export interface Oklch {
  L: number; // 0..1
  C: number; // ~0..0.4
  H: number; // degrees 0..360
}

export function hexToOklch(hex: string): Oklch {
  const [sr, sg, sb] = parse(hex);
  const r = srgbToLinear(sr);
  const g = srgbToLinear(sg);
  const b = srgbToLinear(sb);

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const C = Math.hypot(a, bb);
  let H = (Math.atan2(bb, a) * 180) / Math.PI;
  if (H < 0) H += 360;
  return { L, C, H };
}

export function oklchToHex(L: number, C: number, H: number): string {
  const hr = (H * Math.PI) / 180;
  const a = C * Math.cos(hr);
  const b = C * Math.sin(hr);

  const l_ = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m_ = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s_ = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;

  const r = linearToSrgb(4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_);
  const g = linearToSrgb(-1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_);
  const bl = linearToSrgb(-0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_);
  return toHex(r, g, bl);
}

// Institution envelope (palette.md §07): keep hue, render the quartet at these
// L steps with chroma ~0.135 — a touch more vivid so normalized bank brand colors
// read with the same punch as the refreshed (more-vivid) category palette.
const ENVELOPE_CHROMA = 0.135;
const ENVELOPE_L = { surface: 0.97, border: 0.86, accent: 0.6, text: 0.42 } as const;
const NEUTRAL_HUE_CHROMA_FLOOR = 0.03; // below this the brand is achromatic → neutral

export interface BankQuartet {
  surface: string;
  border: string;
  accent: string;
  text: string;
}

/**
 * Normalize an external brand hex into the system envelope. Returns null when the
 * brand is effectively achromatic (black/gray/white) — the caller routes those to
 * the neutral ramp, since there's no meaningful hue to keep.
 */
export function normalizeExternalColor(hex: string): BankQuartet | null {
  const { C, H } = hexToOklch(hex);
  if (C < NEUTRAL_HUE_CHROMA_FLOOR) return null;
  return {
    surface: oklchToHex(ENVELOPE_L.surface, ENVELOPE_CHROMA * 0.35, H),
    border: oklchToHex(ENVELOPE_L.border, ENVELOPE_CHROMA * 0.7, H),
    accent: oklchToHex(ENVELOPE_L.accent, ENVELOPE_CHROMA, H),
    text: oklchToHex(ENVELOPE_L.text, ENVELOPE_CHROMA, H),
  };
}
